import { useEffect, useRef } from 'react';
import { useParams } from 'next/navigation';
import { useStore } from 'zustand';
import { IProductData, TPathParams, getCompleteSelectionData } from '@/utils';
import { IS_PREVIEW } from '@/utils/env';
import useStoreContext from '@/hooks/useStoreContext';
import {
  pushToElevarDataLayer,
  getUserProperties,
  transformProductToElevar,
  getReferrerList,
} from './utils';
import { ElevarProductViewEvent } from './types';

/**
 * Elevar product view tracking hook.
 * Fires dl_view_item event when a user views a product page.
 * Should be used alongside your existing useItemViewedGoogleTag.
 */
export const useElevarProductView = (selectedProduct: IProductData) => {
  const store = useStoreContext();
  if (!store) throw new Error('Missing Provider in the tree');

  const params = useParams<TPathParams>();
  const modelData = useStore(store, (s) => s.modelData);
  const lastFiredStateRef = useRef<string>('');

  const {
    completeSelectionState: { isComplete },
  } = getCompleteSelectionData({
    data: modelData,
  });

  useEffect(() => {
    if (IS_PREVIEW) return;
    if (!selectedProduct) return;

    // Create a stable identifier to prevent duplicate firing
    const stableId = `${selectedProduct.id || selectedProduct.sku || 'unknown'}-${isComplete}`;

    // Only fire if this is a different state than last time
    if (lastFiredStateRef.current === stableId) {
      return;
    }

    lastFiredStateRef.current = stableId;

    const userProperties = getUserProperties();
    const referrerList = getReferrerList();

    const elevarProduct = transformProductToElevar(
      selectedProduct,
      params,
      isComplete,
      1, // position
      referrerList
    );

    const event: ElevarProductViewEvent = {
      event: 'dl_view_item',
      user_properties: userProperties,
      ecommerce: {
        currencyCode: 'USD',
        detail: {
          actionField: {
            list: referrerList,
            action: 'detail',
          },
          products: [elevarProduct],
        },
      },
    };

    pushToElevarDataLayer(event);
  }, [params, selectedProduct, isComplete]);
};

export default useElevarProductView;
